<script setup>
import { ref, computed } from 'vue'

import { getI18n } from '@/plugins/i18n'
import { formatShortDateTime } from '@/lib/utils'
import { reportPeriodLabel, useReportFormatter, useReportSubject } from '@/composables/report'
import { useAccountingStore } from '@/stores/accounting.store'
import { usePortalStore } from '@/stores/portal.store'

import BottomSheetDialog from '@/components/shared/BottomSheetDialog.vue'
import KeyValueList from '@/components/shared/KeyValueList.vue'

const props = defineProps({
  build: {
    type: Object,
    required: true,
  },
  report: {
    type: Object,
    default: null,
  },
})

const i18n = getI18n()
const accountingStore = useAccountingStore()
const portalStore = usePortalStore()
const formatter = useReportFormatter()
const { subjectLabel } = useReportSubject()
const { reportSectionKeyEnum, reportTimeBasisEnum } = accountingStore

const DAY_SECTIONS = Object.freeze([reportSectionKeyEnum.DAILY, reportSectionKeyEnum.HOURS])
const TIME_SECTIONS = Object.freeze([...DAY_SECTIONS, reportSectionKeyEnum.PROJECTS, reportSectionKeyEnum.PROJECTS_CLIENTS])

const showDetails = ref(false)

const sectionOf = (key) => props.build.sections.find(section => section.key === key)
const duration = (minutes) => formatter.duration(minutes, props.build)
const money = (value) => formatter.money(value, props.build.reference_currency)

const timeTotals = computed(() => props.build.sections.find(section => TIME_SECTIONS.includes(section.key) && section.totals?.net != null)?.totals || {})
const days = computed(() => {
  const dayDates = new Set(props.build.sections
    .filter(section => DAY_SECTIONS.includes(section.key))
    .flatMap(section => section.groups.flatMap(group => group.rows.map(row => row.date))))
  return props.report?.total_days ?? (dayDates.size || null)
})
const expenses = computed(() => {
  const expenseSections = accountingStore.expenseSectionKeys.map(sectionOf).filter(Boolean)
  return (expenseSections.length ? expenseSections.reduce((sum, section) => sum + Number(section.totals?.amount_reference || 0), 0) : null)
    ?? sectionOf(reportSectionKeyEnum.PROJECTS_CLIENTS)?.totals?.expenses_reference
    ?? sectionOf(reportSectionKeyEnum.PROJECTS)?.totals?.expenses_reference
})

const tiles = computed(() => [
  { key: 'net', label: i18n.t('print.report.columns.net'), value: duration(timeTotals.value.net) },
  { key: 'days', label: i18n.t('print.report.columns.days'), value: days.value },
  { key: 'expenses', label: i18n.t('print.report.columns.expenses'), value: expenses.value ? money(expenses.value) : '' },
].filter(tile => tile.value != null && tile.value !== ''))

const detailItems = computed(() => [
  { label: i18n.t('print.report.columns.gross'), value: duration(timeTotals.value.gross) },
  { label: i18n.t('print.report.columns.pause'), value: duration(timeTotals.value.pause) },
  { label: i18n.t('print.report.columns.travel'), value: duration(timeTotals.value.travel) },
  { label: i18n.t('print.report.columns.night'), value: duration(timeTotals.value.night) },
  { label: i18n.t('print.report.columns.invoiced'), value: money(sectionOf(reportSectionKeyEnum.INVOICES)?.totals?.total_reference) },
  ...(props.report ? [
    { label: i18n.t('common.report.subject'), value: subjectLabel(props.report) },
    { label: i18n.t('print.report.period'), value: reportPeriodLabel({ date_from: props.report.date_from, date_to: props.report.date_to }) },
    { label: i18n.t('common.report.createdOn'), value: props.report.created_at ? formatShortDateTime(props.report.created_at) : '' },
    { label: i18n.t('common.report.dataAsOf'), value: props.report.rows_computed_at ? formatShortDateTime(props.report.rows_computed_at) : '' },
    { label: i18n.t('print.report.timeBasis'), value: i18n.t(props.report.time_basis === reportTimeBasisEnum.REPORTED ? 'print.report.timeBasisReported' : 'print.report.timeBasisWork') },
    { label: i18n.t('common.documentLanguage'), value: portalStore.getLocalizedLanguages().find(language => language.code === props.report.language)?.name },
  ] : []),
])
</script>

<template>
  <div class="q-px-sm q-pt-sm">
    <div v-if="tiles.length" class="report-tiles">
      <div
        v-for="(tile, index) in tiles"
        :key="tile.key"
        class="report-tile"
        :class="index === 0 ? 'bg-primary text-white' : 'report-tile--soft'"
      >
        <div class="report-tile__label">{{ tile.label }}</div>
        <div class="report-tile__value">{{ tile.value }}</div>
      </div>
    </div>
    <div class="row justify-end">
      <q-btn
        flat
        dense
        no-caps
        size="sm"
        color="primary"
        icon-right="mdi-chevron-right"
        :label="i18n.t('common.details')"
        @click="showDetails = true"
      />
    </div>
    <bottom-sheet-dialog v-model="showDetails" :title="i18n.t('common.details')">
      <key-value-list :items="detailItems" />
    </bottom-sheet-dialog>
  </div>
</template>

<style scoped lang="scss">
.report-tiles {
  display: grid;
  grid-template-columns: 1.3fr repeat(2, 1fr);
  gap: 0.5rem;
}

.report-tile {
  min-width: 0;
  padding: 0.5rem 0.75rem;
  border-radius: $app-border-radius;
}

.report-tile--soft {
  background: color-mix(in srgb, var(--q-primary) 10%, transparent);
  color: var(--q-primary);
}

.report-tile__label {
  font-size: 0.6875rem;
  opacity: 0.85;
}

.report-tile__value {
  font-size: 1.125rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
